import { supabase } from '../supabase';

/* ============================================
   CONSTANTS
   ============================================ */

export const GOAL_TYPES = [
  { value: 'career',  label: 'Career Goal',
    placeholder: 'e.g., Become a Staff GenAI Engineer' },
  { value: 'skill',   label: 'Skill Goal',
    placeholder: 'e.g., Master MLOps + LangChain' },
  { value: 'project', label: 'Project Goal',
    placeholder: 'e.g., Ship Curx AI to 1000 users' },
];

export const GOAL_TYPE_BY_VALUE = GOAL_TYPES.reduce((acc, g) => {
  acc[g.value] = g;
  return acc;
}, {});

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];

/* ============================================
   MONTH MATH
   ============================================ */

// Sequential month-of-journey index (1-12) given today's date
export function getCurrentMonthIndex(journey) {
  if (!journey) return 1;
  const today = new Date();
  const monthsSinceStart =
    (today.getFullYear() - journey.year) * 12 +
    (today.getMonth() + 1 - journey.start_month) + 1;
  return Math.max(1, Math.min(12, monthsSinceStart));
}

// "September 2026" for journey-relative month index
export function monthLabel(journey, monthIdx) {
  if (!journey) return '';
  const offset = (journey.start_month - 1) + (monthIdx - 1);
  const monthOfYear = ((offset % 12) + 12) % 12;
  const yearOffset = Math.floor(offset / 12);
  return `${MONTH_NAMES[monthOfYear]} ${journey.year + yearOffset}`;
}

// "Sep" short label
export function monthShortLabel(journey, monthIdx) {
  if (!journey) return '';
  const offset = (journey.start_month - 1) + (monthIdx - 1);
  const monthOfYear = ((offset % 12) + 12) % 12;
  return MONTH_NAMES[monthOfYear].slice(0, 3);
}

// "2026-09" for the garden_letters.month TEXT column
export function formatMonthYYYYMM(journey, monthIdx) {
  if (!journey) return '';
  const offset = (journey.start_month - 1) + (monthIdx - 1);
  const monthOfYear = ((offset % 12) + 12) % 12;
  const yearOffset = Math.floor(offset / 12);
  const year = journey.year + yearOffset;
  const m = monthOfYear + 1;
  return `${year}-${String(m).padStart(2, '0')}`;
}

export function letterStatusForMonth(monthIdx, currentIdx, letters) {
  const hasLetter = (letters || []).some(l => l.month_index === monthIdx);
  if (hasLetter) return 'tended';
  if (monthIdx === currentIdx) return 'current';
  if (monthIdx < currentIdx) return 'untended';
  return 'future';
}

/* ============================================
   PROMPTS
   ============================================ */

export const THEME_GENERATION_PROMPT = (year, startMonth, goals, skillBlock, profileBlock) => `You are designing a 12-month thematic arc for a professional's year-long growth journey.

JOURNEY START: ${MONTH_NAMES[startMonth - 1]} ${year}
GOALS:
${goals.map((g, i) => `${i + 1}. [${(g.type || '').toUpperCase()}] ${g.title}${g.description ? ` — ${g.description}` : ''}`).join('\n')}

CONTEXT:
${skillBlock}${profileBlock}

Generate 12 monthly themes in sequence (month 1 = ${MONTH_NAMES[startMonth - 1]} ${year}, month 2 = next month, etc.) that map onto a natural growth arc:
- Foundations / orientation in early months
- Application / experimentation in middle months
- Mastery / synthesis in later months
- Reflection / harvest near the end

Each theme: 1-3 evocative words, plus a 1-sentence description connecting to their goals.

Output ONLY valid JSON, no markdown:

{
  "themes": {
    "1":  {"theme": "<1-3 words>", "description": "<1 sentence>"},
    "2":  {"theme": "<1-3 words>", "description": "<1 sentence>"},
    "3":  {"theme": "<1-3 words>", "description": "<1 sentence>"},
    "4":  {"theme": "<1-3 words>", "description": "<1 sentence>"},
    "5":  {"theme": "<1-3 words>", "description": "<1 sentence>"},
    "6":  {"theme": "<1-3 words>", "description": "<1 sentence>"},
    "7":  {"theme": "<1-3 words>", "description": "<1 sentence>"},
    "8":  {"theme": "<1-3 words>", "description": "<1 sentence>"},
    "9":  {"theme": "<1-3 words>", "description": "<1 sentence>"},
    "10": {"theme": "<1-3 words>", "description": "<1 sentence>"},
    "11": {"theme": "<1-3 words>", "description": "<1 sentence>"},
    "12": {"theme": "<1-3 words>", "description": "<1 sentence>"}
  }
}

Themes should feel like chapter titles — evocative, not corporate. Examples of tone: "Roots Deepening", "Quiet Practice", "First Bloom", "The Long Arc", "Harvest Light".`;

export const GARDEN_COACH_SYSTEM = (userName, monthLbl, themeText, themeDesc, goals, skillBlock) => `You are the Garden coach for Curx AI, a warm, attentive guide who helps ${userName} reflect on their growth, month by month.

THIS MONTH: ${monthLbl}
THIS MONTH'S THEME: ${themeText}${themeDesc ? ` — ${themeDesc}` : ''}

THEIR GOALS FOR THE YEAR:
${goals.map((g, i) => `${i + 1}. [${(g.type || '').toUpperCase()}] ${g.title}`).join('\n')}

THEIR SKILLS:
${skillBlock}

Your job: have a gentle, perceptive conversation with ${userName} about what happened this past month. You're not a therapist — you're a botanical journal coming to life, helping them notice what grew and what didn't.

Rules:
1. Ask ONE thing at a time. Never stack questions.
2. Reflect briefly what you heard before asking the next thing.
3. Use botanical metaphors lightly — root, sprout, prune, sunlight — but don't overdo it.
4. Keep messages SHORT — 2-3 sentences max.
5. Across the conversation, gently gather:
   - What grew this month (progress, wins)
   - What withered (setbacks, things that didn't land)
   - Unexpected sprouts (surprises, emergent learnings)
   - What they learned about themselves
   - What needs more sunlight next month
6. Use ${userName}'s name occasionally, not every message.

After 4-5 thoughtful exchanges, signal you're ready by ending your message with EXACTLY:
[READY_FOR_GARDEN_LETTER]

Begin with a warm, theme-aware opening.`;

export const LETTER_GENERATION_PROMPT = (userName, monthLbl, themeText, themeDesc, goals, transcript) => `You are Curx AI's Garden module, writing a monthly letter to ${userName}.

MONTH: ${monthLbl}
THEME: ${themeText}${themeDesc ? ` — ${themeDesc}` : ''}

YEAR-LONG GOALS:
${goals.map((g, i) => `${i + 1}. [${(g.type || '').toUpperCase()}] ${g.title}${g.description ? ` — ${g.description}` : ''}`).join('\n')}

CONVERSATION FROM THIS MONTH:
${transcript}

Write a personal letter back to ${userName} reflecting on this month. The letter should:
1. Open by naming the month and the theme — calm, grounded tone.
2. Be 4-5 paragraphs, narrative prose — no bullets, no headers.
3. Reference SPECIFIC things they said. Quote or paraphrase honestly.
4. Connect what happened to their bigger year-long goals.
5. Name what's growing, what's still becoming, what's been pruned.
6. Use botanical language where it fits naturally — never forced.
7. End with ONE intentional question or focus for next month — pointed, specific.
8. Sign with: a final line "Tended with care," then on the next line: "Curx AI"

Output ONLY the letter text. No JSON. No markdown headers. Just flowing prose.`;

export const YEAR_REVIEW_PROMPT = (userName, journey, themesObj, lettersList) => {
  const startMonthName = MONTH_NAMES[journey.start_month - 1];
  const endIdx = ((journey.start_month - 2 + 12) % 12);
  const endMonthName = MONTH_NAMES[endIdx];
  const endYear = journey.start_month === 1 ? journey.year : journey.year + 1;
  return `You are Curx AI's Garden module, writing a Year in Review for ${userName}.

JOURNEY: ${startMonthName} ${journey.year} → ${endMonthName} ${endYear}

GOALS THEY SET AT THE START:
${(journey.goals || []).map((g, i) => `${i + 1}. [${(g.type || '').toUpperCase()}] ${g.title}${g.description ? ` — ${g.description}` : ''}`).join('\n')}

THEMES (12 months):
${Object.entries(themesObj || {}).sort((a, b) => Number(a[0]) - Number(b[0])).map(([k, v]) => `Month ${k}: ${v.theme}${v.description ? ` — ${v.description}` : ''}`).join('\n')}

ALL 12 MONTHLY LETTERS (in order):
${lettersList.map((l) => `===== MONTH ${l.month_index} =====\n${l.letter_content}`).join('\n\n')}

Write a comprehensive Year-in-Review essay for ${userName}. This should feel like the closing chapter of their year-long journal.

Structure (use these exact section headers, each on their own line, with prose between):

The Arc of the Year
(2-3 paragraphs: how they entered the year, what they were chasing, the overall shape of their growth)

What Grew
(2-3 paragraphs: specific evidence from the letters of skills sharpened, projects shipped, identity shifted)

What Was Pruned
(1-2 paragraphs: things let go of, paths not taken, beliefs revised)

The Pattern Underneath
(2 paragraphs: a single insight or theme that runs through the whole year — the deeper "story" of who they became)

A Question for the Year Ahead
(1 short paragraph ending with a single pointed question)

Be SPECIFIC. Quote moments. Reference letters by their month name and theme. Make this a document they'd want to print, frame, and keep.

Output ONLY the essay text. No JSON. No markdown. Plain prose with the section headers exactly as written above.`;
};

const YEAR_REVIEW_HEADERS = [
  'The Arc of the Year',
  'What Grew',
  'What Was Pruned',
  'The Pattern Underneath',
  'A Question for the Year Ahead',
];

export function parseYearReview(text) {
  if (!text) return [];
  const lines = String(text).split('\n');
  const sections = [];
  let current = null;
  for (const raw of lines) {
    const line = raw.trim();
    if (YEAR_REVIEW_HEADERS.includes(line)) {
      if (current) sections.push(current);
      current = { title: line, body: '' };
    } else if (current) {
      current.body += raw + '\n';
    }
  }
  if (current) sections.push(current);
  return sections.map(s => ({ ...s, body: s.body.trim() }));
}

/* ============================================
   SUPABASE
   ============================================ */

export async function loadJourney(userId, year) {
  if (!userId) return null;
  const { data, error } = await supabase
    .from('garden_journeys')
    .select('*')
    .eq('user_id', userId)
    .eq('year', year)
    .limit(1);
  if (error) throw error;
  return (data && data[0]) || null;
}

export async function loadAllJourneys(userId) {
  if (!userId) return [];
  const { data, error } = await supabase
    .from('garden_journeys')
    .select('*')
    .eq('user_id', userId)
    .order('year', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function createJourney({ user_id, year, start_month, goals, themes }) {
  if (!user_id) throw new Error('Missing user_id');
  const payload = {
    user_id,
    year,
    start_month,
    goals: goals || [],
    themes: themes || {},
    status: 'active',
  };
  const { data, error } = await supabase
    .from('garden_journeys')
    .insert(payload)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Save didn't write any rows. Check your RLS policy for INSERT on garden_journeys.");
  }
  return data[0];
}

export async function updateThemes(journeyId, themes) {
  const { data, error } = await supabase
    .from('garden_journeys')
    .update({ themes, updated_at: new Date().toISOString() })
    .eq('id', journeyId)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Update didn't write any rows. Check your RLS policy for UPDATE on garden_journeys.");
  }
  return data[0];
}

// Hard delete journey — letters cascade automatically via FK
export async function deleteJourney(journeyId) {
  const { data, error } = await supabase
    .from('garden_journeys')
    .delete()
    .eq('id', journeyId)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Delete didn't affect any rows. Check your RLS policy for DELETE on garden_journeys.");
  }
}

export async function loadLetters(journeyId) {
  if (!journeyId) return [];
  const { data, error } = await supabase
    .from('garden_letters')
    .select('*')
    .eq('journey_id', journeyId)
    .order('month_index', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function saveLetter({ user_id, journey_id, month_index, month, letter_content, referenced_data }) {
  if (!user_id) throw new Error('Missing user_id');
  if (!journey_id) throw new Error('Missing journey_id');
  const payload = {
    user_id,
    journey_id,
    month_index,
    month,
    letter_content,
    referenced_data: referenced_data || {},
  };
  const { data, error } = await supabase
    .from('garden_letters')
    .insert(payload)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Save didn't write any rows. Check your RLS policy for INSERT on garden_letters.");
  }
  return data[0];
}

export async function saveYearReview(journeyId, reviewText) {
  const { data, error } = await supabase
    .from('garden_journeys')
    .update({
      year_review_text: reviewText,
      year_review_generated_at: new Date().toISOString(),
      status: 'completed',
      updated_at: new Date().toISOString(),
    })
    .eq('id', journeyId)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Update didn't write any rows. Check your RLS policy for UPDATE on garden_journeys.");
  }
  return data[0];
}

/* ============================================
   AI CALLS
   ============================================ */

async function callClaude(prompt, model) {
  const response = await fetch('/api/claude', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, model }),
  });
  const data = await response.json();
  return (data?.content || []).map(i => i.text || '').join('').trim();
}

export async function generateThemes({ year, start_month, goals, skillBlock, profileBlock }) {
  const text = await callClaude(
    THEME_GENERATION_PROMPT(year, start_month, goals, skillBlock, profileBlock),
    'sonnet'
  );
  const parsed = tryParseJSONObject(text);
  if (!parsed || !parsed.themes || typeof parsed.themes !== 'object') {
    throw new Error('Theme generation returned invalid JSON');
  }
  const normalized = {};
  for (let i = 1; i <= 12; i++) {
    const t = parsed.themes[String(i)] || parsed.themes[i] || {};
    normalized[i] = {
      theme: String(t.theme || `Month ${i}`).trim(),
      description: String(t.description || '').trim(),
    };
  }
  return normalized;
}

export async function generateLetter({ userName, journey, monthIdx, transcript }) {
  const lbl = monthLabel(journey, monthIdx);
  const themeData = (journey.themes && (journey.themes[monthIdx] || journey.themes[String(monthIdx)])) || { theme: '', description: '' };
  const text = await callClaude(
    LETTER_GENERATION_PROMPT(userName, lbl, themeData.theme, themeData.description, journey.goals || [], transcript),
    'sonnet'
  );
  if (!text || text.length < 50) throw new Error('Letter generation returned empty or too-short content');
  return text;
}

export async function generateYearReview({ userName, journey, letters }) {
  const text = await callClaude(
    YEAR_REVIEW_PROMPT(userName, journey, journey.themes || {}, letters),
    'sonnet'
  );
  if (!text || text.length < 200) throw new Error('Year review generation returned empty or too-short content');
  return text;
}

/* ============================================
   HELPERS
   ============================================ */

function tryParseJSONObject(text) {
  if (!text) return null;
  try { return JSON.parse(text); } catch (_) { /* fallthrough */ }
  const match = String(text).match(/\{[\s\S]*\}/);
  if (match) {
    try { return JSON.parse(match[0]); } catch (__) { /* fallthrough */ }
  }
  return null;
}

export function formatLetterDate(timestamp) {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}
